import { Component, Input } from '@angular/core';
import { agreementScheduledPayments } from '../agreement-scheduled-payments';

@Component({
  selector: 'app-agreement-template-quebec',
  templateUrl: './agreement-template-quebec.component.html',
  styleUrls: ['./agreement-template-quebec.component.css']
})
export class AgreementTemplateQuebecComponent {
  @Input() entityData: any;
  @Input() studentAccountEntityData: any;
  @Input() studioInformationConfig: any;
  @Input() enrollmentForm: any;
  @Input() isTaxInclusive = false;
  @Input() displaySignatures = true;
  @Input() displaySigned = true;
  @Input() _enrollmentEntityData: any;

  // Quebec sales tax rates, used only to apportion the recorded tax amount
  // between the two columns the paper form shows (GST 5% / TPS 9.975%).
  private readonly GST_RATE = 5;
  private readonly QST_RATE = 9.975;

  get primaryContact(): any {
    const contacts = this.studentAccountEntityData?.field_contacts;
    if (!contacts) return null;
    if (Array.isArray(contacts)) {
      const active = contacts.filter(c => c.field_archived !== '1' && c.field_archived !== 1);
      return active[0] || contacts[0] || null;
    }
    return contacts;
  }

  /** Second contact on the account, for the élève 2 block (couples). */
  get secondaryContact(): any {
    const contacts = this.studentAccountEntityData?.field_contacts;
    if (!Array.isArray(contacts)) return null;
    const active = contacts.filter(c => c.field_archived !== '1' && c.field_archived !== 1);
    return active[1] || null;
  }

  /**
   * Total the student owes for this enrollment. field_enrollment_total_price is
   * the pre-tax total (lessons - discount) in both tax modes; field_taxes is the
   * tax amount. When the studio's taxes are exclusive the tax sits ON TOP of the
   * price and must be added; when inclusive it is already inside the price.
   */
  calculateTotalTuition(): number {
    const price = parseFloat(this.entityData?.field_enrollment_total_price) || 0;
    return this.isTaxInclusive ? price : price + this.getTaxAmount();
  }

  /** Enrollment-level tax amount (field_taxes), regardless of tax mode. */
  getTaxAmount(): number {
    return parseFloat(this.entityData?.field_taxes) || 0;
  }

  /** Per-payment amount actually payable: adds the row's tax when taxes are exclusive. */
  private payableAmount(row: any): number {
    const amount = parseFloat(this.fieldValue(row, 'field_payment_amount')) || 0;
    if (this.isTaxInclusive) return amount;
    return amount + (parseFloat(this.fieldValue(row, 'field_tax_amount')) || 0);
  }

  /**
   * Tuition before discount and tax. Lesson packages = lesson price x count;
   * zero-lesson enrollments (Miscellaneous, Sundry, services) price the whole
   * thing in field_enrollment_lesson_price / field_total_lessons_price, so fall
   * back to those rather than printing $0.00 tuition.
   */
  getOriginalPackageCost(): number {
    const price = parseFloat(this.entityData?.field_enrollment_lesson_price) || 0;
    const count = parseFloat(this.entityData?.field_enrollment_lesson_count) || 0;
    if (price > 0 && count > 0) {
      return price * count;
    }
    const gross = parseFloat(this.entityData?.field_total_lessons_price) || 0;
    if (gross > 0) return gross;
    if (price > 0) return price;
    // Last resort: net price plus whatever discount was taken off it.
    const net = parseFloat(this.entityData?.field_enrollment_total_price) || 0;
    const discount = typeof (this as any).getDiscountAmount === 'function'
      ? ((this as any).getDiscountAmount() || 0)
      : (parseFloat(this.entityData?.field_discount) || 0);
    return net > 0 ? net + (this.entityData?.field_discount_type === 'percentage' ? 0 : discount) : 0;
  }

  getDiscountAmount(): number {
    const discount = parseFloat(this.entityData?.field_discount) || 0;
    if (this.entityData?.field_discount_type === 'percentage') {
      return this.getOriginalPackageCost() * (discount / 100);
    }
    return discount;
  }

  /** Frais de scolarité: tuition net of discount, before tax. */
  /** Pre-tax subtotal: strips the included tax portion when taxes are inclusive. */
  getSubtotal(): number {
    const net = this.getOriginalPackageCost() - this.getDiscountAmount();
    return this.isTaxInclusive ? net - this.getTaxAmount() : net;
  }

  getTotalTaxes(): number {
    return parseFloat(this.entityData?.field_taxes) || 0;
  }

  /** Federal GST share of the recorded tax amount. */
  getGstAmount(): number {
    return this.getTotalTaxes() * (this.GST_RATE / (this.GST_RATE + this.QST_RATE));
  }

  /** Quebec TPS/QST share of the recorded tax amount. */
  getQstAmount(): number {
    return this.getTotalTaxes() * (this.QST_RATE / (this.GST_RATE + this.QST_RATE));
  }

  get enrollmentTypeState() {
    const category = this.entityData?.field_category;
    const targetId = Array.isArray(category) ? category[0]?.target_id?.toString() : category?.target_id?.toString() || category?.toString();

    const map: { [key: string]: string } = {
      '50': 'original', '49': 'original',
      '51': 'extension', '543': 'extension', '546': 'extension',
      '65': 'renewal',
      '1421': 'original', '70': 'original', '1393': 'original', '1429': 'original'
    };

    const mapped = targetId ? map[targetId] : null;
    return {
      original: mapped === 'original',
      extension: mapped === 'extension',
      renewal: mapped === 'renewal'
    };
  }

  get isInstallmentAgreement(): boolean {
    const structure = this.enrollmentForm?.get?.('field_payments_structure')?.value
      || this.entityData?.field_payments_structure;
    return structure && structure !== 'paid_in_full';
  }

  get structureType(): string | null {
    return this.enrollmentForm?.get?.('field_payments_structure_type')?.value
      || this.entityData?.field_payments_structure_type
      || null;
  }

  /** Scheduled payment rows normalized to {amount, date, down, lessonsRemaining}, date ascending. */
  private scheduledPaymentRows(): { amount: number; date: string | null; down: boolean; lessonsRemaining: number | null }[] {
    let raw = agreementScheduledPayments(this.enrollmentForm?.get?.('field_scheduled_payments')?.value);
    if (raw.length === 0) {
      raw = agreementScheduledPayments(this.entityData?.field_scheduled_payments);
    }

    return raw
      .map(r => {
        const rem = this.fieldValue(r, 'field_number_lesson_remaining');
        return {
          amount: this.payableAmount(r),
          date: this.fieldValue(r, 'field_payment_date') || null,
          down: ['1', 1, true].includes(this.fieldValue(r, 'field_down_payment')),
          lessonsRemaining: (rem === null || rem === undefined || rem === '') ? null : (parseInt(rem, 10) || 0)
        };
      })
      .filter(r => r.amount > 0)
      .sort((x, y) => !x.date ? 1 : !y.date ? -1 : x.date.localeCompare(y.date));
  }

  /** Paid today: the down-payment row, or the first payment when no down row exists. */
  getPaidToday(): number {
    const rows = this.scheduledPaymentRows();
    const down = rows.find(r => r.down);
    return down?.amount ?? rows[0]?.amount ?? 0;
  }

  /** Remaining installments after today's payment. */
  get remainingInstallments(): { amount: number; date: string | null }[] {
    const rows = this.scheduledPaymentRows();
    if (!rows.length) return [];
    const downIndex = rows.findIndex(r => r.down);
    const todayIndex = downIndex >= 0 ? downIndex : 0;
    return rows.filter((_, i) => i !== todayIndex);
  }

  get remainingAmountsEqual(): boolean {
    const rows = this.remainingInstallments;
    if (rows.length <= 1) return true;
    return rows.every(r => Math.abs(r.amount - rows[0].amount) < 0.01);
  }

  /**
   * "à chaque N leçons privées": lessons per payment for by-the-lesson plans,
   * derived from the field_number_lesson_remaining countdown (gap between
   * consecutive values), same as the Italian Section B fill.
   */
  get lessonsPerPayment(): number | null {
    if (this.structureType !== 'by_the_lesson') return null;
    const remaining = this.scheduledPaymentRows()
      .map(r => r.lessonsRemaining)
      .filter((v): v is number => v !== null && v > 0)
      .sort((a, b) => b - a);
    if (remaining.length >= 2) {
      const gap = remaining[0] - remaining[1];
      if (gap > 0) return gap;
    }
    return null;
  }

  /** French cadence for the "ou ___" blank on weekly/monthly plans. */
  get cadenceFrench(): string | null {
    if (this.structureType === 'weekly') return 'chaque semaine';
    if (this.structureType === 'monthly') return 'chaque mois';
    return null;
  }

  private fieldValue(obj: any, fieldName: string): any {
    const v = obj?.[fieldName];
    if (Array.isArray(v)) return v[0]?.value ?? v[0];
    if (v && typeof v === 'object' && 'value' in v) return v.value;
    return v;
  }
}
